import type { PaletteOptions } from '@mui/material';

// ─── Brand tokens (Figma) ────────────────────────────────────────────────────
// primary.main        #CBE88C  — button fill, selected option bg
// primary.dark        #80AA22  — icons, hover darken, active states
// primary.light       #E6F4C0  — subtle tints
// primary.contrastText #000000 — button label text
//
// background.default  #F2F3EE  — page bg (Figma green-50)
// background.paper    #FFFFFF  — card / panel surface
//
// surface.bg          #F6F7F3  — elevated surface bg (requirements bar, inner cards)
// surface.border      #E4E6DB  — border for elevated surfaces
//
// text.primary        #00332D  — headings, body
// text.secondary      #666666  — supporting text
// text.disabled       #8D8D8D  — tertiary / hints
// text.strong         #0E121B  — maximum contrast labels (notes panel titles)
// text.icon           #525866  — icon labels, requirement captions
// text.placeholder    #9E9E9E  — input placeholders
//
// divider             #F2F2F2  — card borders, page frame borders
// ─────────────────────────────────────────────────────────────────────────────

export const lightPalette: PaletteOptions = {
  mode: 'light',
  gradients: {
    // light(#E6F4C0) → main(#CBE88C) → dark(#80AA22)
    icon: 'linear-gradient(135deg, #E6F4C0 0%, #CBE88C 50%, #80AA22 100%)',
    // very-light → light — subtle badge/chip background
    iconSoft: 'linear-gradient(135deg, #F3FAE0 0%, #E6F4C0 100%)',
  },
  primary: {
    main: '#CBE88C',
    light: '#E6F4C0',
    dark: '#80AA22',
    contrastText: '#000000',
  },
  secondary: {
    main: '#525866',
    light: '#8A90A0',
    dark: '#333844',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#2E7D32',
    light: '#66BB6A',
    dark: '#1B5E20',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#ED6C02',
    light: '#FF9800',
    dark: '#E65100',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#D32F2F',
    light: '#EF5350',
    dark: '#C62828',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#0288D1',
    light: '#29B6F6',
    dark: '#01579B',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F2F3EE', // Figma green-50 — page canvas bg
    paper: '#FFFFFF', // Card / panel surface
  },
  surface: {
    bg: '#F6F7F3', // Elevated surface (requirements bar, inner cards)
    border: '#E4E6DB', // Border for elevated surfaces
    dialogBg: '#F8F8F8', // Modal paper background
    dialogIconBg: '#F7F6F3', // Dialog icon badge background
    dialogIconBorder: '#EDEAE3', // Dialog icon badge border
    clockAccent: '#D1B564', // Timeout clock accent
    optionSelectedBg: '#F7FCEE', // Selected option card background
    optionBorder: '#F0F0F0', // Option card default border + submitted bar border
    iDontKnowBg: '#E7EAE2', // "I don't know" button background
    radioActive: '#698C1C', // Active radio outer ring + "Close" panel button (Figma #698c1c)
    radioInactive: '#E1E4EA', // Inactive radio outer ring bg
    executeText: '#405511', // Execute Code button text + Play icon fill (Figma)
    codeActionBorder: '#D6DAD3', // Clear Code / action button border in code editor header
    codePanelHeader: '#E6E8E3', // Header bar bg inside CODE EDITOR and PREVIEW panels
    codePanelBg: '#F5F6F4', // Code editor panel container background
    dropzoneBg: '#FAFBF9', // File upload dropzone background
    dropzoneBorder: '#DDE0D1', // File upload dropzone dashed border
    browseButtonBorder: '#E8EBE0', // "Browse File" button border
    iconColor: '#80AA22', // Default icon color = primary.dark (overridden to primary.main when branding is active)
  },
  text: {
    primary: '#00332D',
    secondary: '#666666',
    disabled: '#8D8D8D',
    strong: '#0E121B',
    icon: '#525866',
    placeholder: '#9E9E9E',
  },
  divider: '#F2F2F2',
};

export const darkPalette: PaletteOptions = {
  mode: 'dark',
  gradients: {
    // dark-mode: mid-tone → bright → very bright (lighter feel on dark bg)
    icon: 'linear-gradient(135deg, #80AA22 0%, #CBE88C 50%, #E6F4C0 100%)',
    iconSoft: 'linear-gradient(135deg, #1A2920 0%, #2A3D34 100%)',
  },
  primary: {
    main: '#CBE88C',
    light: '#E6F4C0',
    dark: '#80AA22',
    contrastText: '#000000',
  },
  secondary: {
    main: '#8A90A0',
    light: '#B0B7C6',
    dark: '#525866',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#66BB6A',
    light: '#A5D6A7',
    dark: '#2E7D32',
    contrastText: '#000000',
  },
  warning: {
    main: '#FFA726',
    light: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#000000',
  },
  error: {
    main: '#EF5350',
    light: '#E57373',
    dark: '#D32F2F',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#29B6F6',
    light: '#81D4FA',
    dark: '#0288D1',
    contrastText: '#000000',
  },
  background: {
    default: '#0E1A16',
    paper: '#162119',
  },
  surface: {
    bg: '#1A2920',
    border: '#2A3D34',
    dialogBg: '#162119', // Dark-mode modal paper background
    dialogIconBg: '#1A2920', // Dark-mode dialog icon badge background
    dialogIconBorder: '#2A3D34', // Dark-mode dialog icon badge border
    clockAccent: '#DCC27A', // Dark-mode timeout clock accent
    optionSelectedBg: '#1A2E20',
    optionBorder: '#2A3D34',
    iDontKnowBg: '#2A3520',
    radioActive: '#80AA22', // dark-mode equivalent active radio
    radioInactive: '#3A4550', // dark-mode equivalent inactive radio
    executeText: '#80AA22', // dark-mode equivalent
    codeActionBorder: '#3A4550', // dark-mode equivalent
    codePanelHeader: '#2A3D34', // dark-mode equivalent header bar
    codePanelBg: '#1A2920', // dark-mode equivalent panel bg
    dropzoneBg: '#141F1A', // dark-mode dropzone bg
    dropzoneBorder: '#2A3D34', // dark-mode dropzone border
    browseButtonBorder: '#2A3D34', // dark-mode browse button border
    iconColor: '#80AA22', // Default icon color = primary.dark (overridden to primary.main when branding is active)
  },
  text: {
    primary: '#E6F4C0',
    secondary: '#A0ADA8',
    disabled: '#5C6B65',
    strong: '#F0F5EC',
    icon: '#8A90A0',
    placeholder: '#5C6B65',
  },
  divider: '#1E2E28',
};
